import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { initAnalytics } from "@/utils/analytics";

const CONSENT_COOKIE = "hairhelp_cookie_consent";
const MAX_AGE_12M = 60 * 60 * 24 * 365; // 12 mesi in secondi

function readConsent() {
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${CONSENT_COOKIE}=`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match.split("=")[1]));
  } catch {
    return null;
  }
}

function saveConsent(analytics) {
  const value = encodeURIComponent(
    JSON.stringify({ necessary: true, analytics, ts: Date.now() })
  );
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${MAX_AGE_12M}; Path=/; SameSite=Lax${secure}`;
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (!readConsent()) setVisible(true);
  }, []);

  const finish = (analyticsChoice) => {
    saveConsent(analyticsChoice);
    if (analyticsChoice) initAnalytics();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] border-t-2 border-accent bg-black text-white shadow-[0_-8px_30px_rgba(0,0,0,0.35)]"
      role="dialog"
      aria-live="polite"
      aria-label="Informativa sui cookie"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-6xl px-5 py-5 sm:px-6 sm:py-6">
        {!showPrefs ? (
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <p className="max-w-3xl text-sm leading-relaxed text-white/80" data-testid="cookie-banner-text">
              HairHelp utilizza cookie tecnici necessari al funzionamento del sito. Con il tuo consenso
              utilizziamo anche cookie analitici per migliorare l'esperienza. Puoi accettare tutti i cookie
              o gestire le tue preferenze.{" "}
              <Link
                to="/cookie-policy"
                className="font-semibold text-accent underline underline-offset-2 hover:text-accent/80"
                data-testid="cookie-banner-policy-link"
              >
                Cookie Policy
              </Link>
            </p>
            <div className="flex flex-col gap-3 sm:flex-row md:shrink-0">
              <button
                onClick={() => finish(false)}
                data-testid="cookie-reject-all-btn"
                className="min-h-[44px] rounded-full border border-white/30 px-6 py-2.5 text-sm font-semibold text-white/80 transition-colors duration-200 hover:border-white hover:text-white"
              >
                Rifiuta tutti
              </button>
              <button
                onClick={() => setShowPrefs(true)}
                data-testid="cookie-manage-btn"
                className="min-h-[44px] rounded-full border border-accent px-6 py-2.5 text-sm font-semibold text-accent transition-colors duration-200 hover:bg-accent/10"
              >
                Gestisci preferenze
              </button>
              <button
                onClick={() => finish(true)}
                data-testid="cookie-accept-all-btn"
                className="min-h-[44px] rounded-full bg-accent px-6 py-2.5 text-sm font-semibold text-accent-foreground shadow-soft transition-transform duration-300 ease-out hover:scale-105 active:scale-95"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5" data-testid="cookie-prefs-panel">
            <span className="text-xs font-bold uppercase tracking-widest text-accent">
              Gestisci preferenze
            </span>
            <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
              <div>
                <p className="text-sm font-semibold text-white">Cookie tecnici necessari</p>
                <p className="mt-1 text-xs leading-relaxed text-white/60">
                  Indispensabili per il funzionamento del sito. Sempre attivi.
                </p>
              </div>
              <span className="min-h-[44px] shrink-0 self-center rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white/50">
                Sempre attivi
              </span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-white">Cookie analitici</p>
                <p className="mt-1 text-xs leading-relaxed text-white/60">
                  Ci aiutano a capire come viene usato il sito per migliorare l'esperienza.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analytics}
                onClick={() => setAnalytics((v) => !v)}
                data-testid="cookie-analytics-toggle"
                className={`relative min-h-[28px] mt-1 h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${
                  analytics ? "bg-accent" : "bg-white/20"
                }`}
                aria-label="Attiva o disattiva i cookie analitici"
              >
                <span
                  className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-transform duration-200 ${
                    analytics ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={() => finish(false)}
                data-testid="cookie-reject-all-prefs-btn"
                className="min-h-[44px] rounded-full border border-white/30 px-6 py-2.5 text-sm font-semibold text-white/80 transition-colors duration-200 hover:border-white hover:text-white"
              >
                Rifiuta tutti
              </button>
              <button
                onClick={() => finish(analytics)}
                data-testid="cookie-save-prefs-btn"
                className="min-h-[44px] rounded-full bg-accent px-6 py-2.5 text-sm font-semibold text-accent-foreground shadow-soft transition-transform duration-300 ease-out hover:scale-105 active:scale-95"
              >
                Salva preferenze
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CookieBanner;
